import { useTranslation } from "react-i18next";
import { MessagesSquare } from "lucide-react";
import { ApiError } from "@/lib/api";
import { useCompanySettings, useUpdateCompanySettings } from "@/lib/companySettings";
import { Switch } from "@/components/ui/switch";
import { pushToast } from "@/components/Toaster";

/**
 * Company-wide toggle for fleet-to-fleet conversations (accounts DM each other).
 * Lives on the Accounts page (per the "knob lives next to its feature" rule)
 * rather than buried in Settings. Off by default — opt-in.
 */
export function ConversationsToggle() {
  const { t } = useTranslation();
  const settings = useCompanySettings();
  const update = useUpdateCompanySettings();

  const enabled = settings.data?.conversations?.enabled ?? false;
  const includeWarming = settings.data?.conversations?.include_warming ?? false;

  const onToggle = async (next: boolean) => {
    try {
      await update.mutateAsync({ conversations: { enabled: next } });
      pushToast({
        kind: "success",
        text: next
          ? t("accounts.conversations.enabled_on")
          : t("accounts.conversations.enabled_off"),
      });
    } catch (err) {
      pushToast({
        kind: "error",
        text: err instanceof ApiError ? err.detail : t("accounts.error_unknown"),
      });
    }
  };

  const onToggleWarming = async (next: boolean) => {
    try {
      await update.mutateAsync({ conversations: { include_warming: next } });
      pushToast({
        kind: "success",
        text: next
          ? t("accounts.conversations.warming_on")
          : t("accounts.conversations.warming_off"),
      });
    } catch (err) {
      pushToast({
        kind: "error",
        text: err instanceof ApiError ? err.detail : t("accounts.error_unknown"),
      });
    }
  };

  if (!settings.data) return null;

  return (
    <div className="flex items-start gap-3 rounded-lg border border-border bg-bg-elevated/40 px-4 py-3">
      <MessagesSquare size={18} strokeWidth={2} className="mt-0.5 text-fg-muted" aria-hidden="true" />
      <div className="flex-1">
        <Switch
          checked={enabled}
          disabled={update.isPending}
          onCheckedChange={onToggle}
          label={t("accounts.conversations.title")}
          description={t("accounts.conversations.description")}
        />
        {enabled && (
          <div className="mt-3 border-t border-border pt-3">
            <Switch
              checked={includeWarming}
              disabled={update.isPending}
              onCheckedChange={onToggleWarming}
              label={t("accounts.conversations.warming_title")}
              description={t("accounts.conversations.warming_description")}
            />
          </div>
        )}
      </div>
    </div>
  );
}
